import { Truck, Wrench, Gauge, Droplets, Snowflake, ClipboardCheck, Boxes, Sparkles } from "lucide-react";

export const CONTACT = {
  phone: "432 276 7729",
  phoneHref: "tel:+14322767729",
  email: "sales@mobile-maintenance.com",
  address: "4041 S County Rd W, Odessa, TX 79766",
};

export const NAV = [
  { label: "Fleet Services", href: "#services" },
  { label: "Nationwide Coverage", href: "#coverage" },
  { label: "Industrial Expertise", href: "#expertise" },
  { label: "Careers", href: "/careers", route: true },
  { label: "Maintenance Resources", href: "/resources", route: true },
];

export const STATS = [
  { value: "20+", label: "Years In Service" },
  { value: "150+", label: "Field Technicians" },
  { value: "1M+", label: "PMs Performed" },
  { value: "33", label: "States Serviced" },
  { value: "24/7/365", label: "Availability" },
];

export const SERVICES = [
  {
    icon: Wrench,
    title: "Mobile Mechanics",
    tag: "On-Site Repair",
    desc: "Fully-equipped mobile units bring the shop to your yard, jobsite, or roadside, diagnostics and repair without the tow.",
    span: "lg:col-span-2",
  },
  {
    icon: Gauge,
    title: "Preventative Maintenance",
    tag: "Scheduled PM",
    desc: "Structured PM programs that keep every asset in compliance and out of the breakdown lane.",
    span: "",
  },
  {
    icon: Sparkles,
    title: "Steam Radiator Cleaning & Fleet Wash",
    tag: "Cooling & Wash",
    desc: "High-heat steam decontamination and full fleet washing that restore thermal efficiency.",
    span: "",
  },
  {
    icon: Snowflake,
    title: "Dry Ice Radiator Cleaning",
    tag: "Non-Abrasive",
    desc: "Dry-ice blasting clears debris from radiators and coolers with zero water and zero residue.",
    span: "lg:col-span-2",
  },
  {
    icon: Droplets,
    title: "Fluid Top-Off Services",
    tag: "Fluids",
    desc: "Routine fluid checks and top-offs that protect engines, hydraulics, and drivetrains between PMs.",
    span: "",
  },
  {
    icon: ClipboardCheck,
    title: "Quality Control",
    tag: "Inspection",
    desc: "Documented multi-point inspections and reporting that give you total visibility of fleet health.",
    span: "",
  },
  {
    icon: Boxes,
    title: "Inventory Management",
    tag: "Parts & Stock",
    desc: "Managed parts inventory and stocking so the right component is on the truck when it's needed.",
    span: "",
  },
  {
    icon: Truck,
    title: "Total Fleet Maintenance",
    tag: "Full Program",
    desc: "End-to-end maintenance solutions engineered to extend the life and uptime of your entire fleet.",
    span: "",
  },
];

export const MANIFESTO = [
  {
    no: "01",
    title: "Speed",
    desc: "When an asset is down, revenue stops. Our mobile units deploy fast and fix on-site, so your equipment gets back to earning, not waiting in a shop queue.",
  },
  {
    no: "02",
    title: "Scale",
    desc: "150+ technicians nationwide means one partner for your entire national footprint. Consistent process, consistent standards, everywhere you operate.",
  },
  {
    no: "03",
    title: "Precision",
    desc: "One million preventative maintenances taught us the details that matter. Documented QC, managed inventory, and data you can act on.",
  },
];

export const INDUSTRIES = [
  { name: "Construction", desc: "Excavators, loaders, and jobsite fleets kept running through the build cycle." },
  { name: "Transportation", desc: "Trucking and logistics fleets maintained for DOT compliance and uptime." },
  { name: "Energy Service", desc: "Oilfield and energy equipment serviced in the harshest field conditions." },
];

export const STATES = [
  "Texas", "Louisiana", "Oklahoma", "New Mexico", "Virginia", "Tennessee", "Georgia",
  "Arkansas", "Colorado", "Kansas", "Mississippi", "Alabama", "North Carolina",
  "South Carolina", "Florida", "Ohio", "Pennsylvania", "West Virginia",
];

export const PRICING_DISCLAIMER =
  "Rates vary by location, asset class, and travel. Contact us for a firm quote.";

// Each image is used on ONE service only, spread across all 5 categories
// so photos are not clustered in a single section.
// `dark: true` marks naturally-dark photos that need a LIGHTER overlay
// so the image shows through more (title stays white either way).
// banner.png is reserved for the homepage and is not reused here.
export const SERVICE_PHOTOS = {
  // Mobile Maintenance (3/4)
  "on-site-maintenance": { src: "/assets/svc-topoff.jpg" },
  "emergency-breakdown-maintenance": { src: "/assets/svc-topoff-2.jpg" },
  "hot-shot-service": { src: "/assets/svc-mm.jpg", dark: true },
  // PM Services (3/3)
  "pm-programs": { src: "/assets/svc-mobile-mech.jpg" },
  "pm-tracking": { src: "/assets/svc-h-topoff.jpg", dark: true },
  "pm-scheduling": { src: "/assets/svc-maintenance.jpg" },
  // Shop Services (5/6)
  "in-shop-maintenance": { src: "/assets/svc-h-dedicated.jpg", dark: true },
  "heavy-repairs": { src: "/assets/svc-mechanics.jpg" },
  "component-swings": { src: "/assets/svc-crane.jpg" },
  "steam-cleaning": { src: "/assets/svc-steam-wash.jpg" },
  "dry-ice-cleaning": { src: "/assets/svc-h-dryice.jpg" },
  // Inspections (2/2)
  "dot-inspections": { src: "/assets/svc-quality.jpg" },
  "texas-state-inspections": { src: "/assets/svc-quality-2.jpg" },
  // Fleet Management (2/6)
  "maintenance-record-storage": { src: "/assets/svc-h-about.jpg", dark: true },
  "real-time-communication": { src: "/assets/svc-h-control.jpg", dark: true },
};

export const SERVICE_GROUPS = [
  {
    category: "Mobile Maintenance",
    tag: "We bring the shop to you",
    items: [
      {
        slug: "on-site-maintenance",
        title: "On-Site Maintenance",
        desc: "Mobile Maintenance brings the shop to you. Fully-equipped units service your fleet at your yard, jobsite, or facility.",
        benefits: [
          "No downtime hauling equipment to a shop",
          "Service performed where your assets already are",
          "Fully-stocked mobile units and trained techs",
        ],
        pricing: [
          { label: "Mobile Mechanic, fully-equipped service truck", rate: "$175", unit: "/ hour" },
        ],
      },
      {
        slug: "emergency-breakdown-maintenance",
        title: "Emergency & Breakdown Maintenance",
        desc: "Roadside assistance and breakdown response in select areas to get stranded assets moving again fast.",
        benefits: [
          "Rapid response in covered areas",
          "Minimize costly roadside downtime",
          "Diagnostics and repair on the spot",
        ],
        pricing: [
          { label: "Mobile Mechanic response", rate: "$175", unit: "/ hour" },
          { label: "Hot Shot travel", rate: "$3.00", unit: "/ mile" },
        ],
      },
      {
        slug: "dedicated-technicians",
        title: "Dedicated Technicians",
        desc: "Need skilled or specialty mechanics to staff a remote location or finish a project? We embed technicians with your team.",
        benefits: [
          "Skilled and specialty mechanics on demand",
          "Staff remote sites without hiring overhead",
          "Scale coverage up or down per project",
        ],
        pricing: [
          { label: "Dedicated Mechanic, self-sufficient with tools & diagnostics (no truck)", rate: "$95", unit: "/ hour" },
        ],
      },
      {
        slug: "hot-shot-service",
        title: "Hot Shot Service",
        desc: "Urgent parts, tools, and small-load delivery to your jobsite when you need it now, priced by the mile.",
        benefits: [
          "Fast dispatch for time-critical parts",
          "Straightforward per-mile pricing",
          "Direct delivery to yard or jobsite",
        ],
        pricing: [{ label: "Hot Shot delivery", rate: "$3.00", unit: "/ mile" }],
        closer:
          "When the clock is running against your uptime, we treat the delivery like the repair. Same dispatcher, same accountability, same urgency. One driver, one direct route, one live ETA. If a truck rolls out from us, our job is to make sure yours doesn't sit.",
      },
    ],
  },
  {
    category: "PM Services",
    tag: "On-site preventative maintenance",
    items: [
      {
        slug: "pm-programs",
        title: "PM Programs",
        desc: "We build a preventative maintenance program tailored to your fleet, your budget, and your uptime goals to prevent breakdowns before they happen.",
        benefits: [
          "Custom program built around your budget",
          "Fewer unplanned breakdowns",
          "Predictable, budgetable maintenance costs",
        ],
        pricing: [{ label: "Custom PM Package · top-5 most common asset types", rate: "Call for Quote", unit: "" }],
      },
      {
        slug: "pm-tracking",
        title: "PM Tracking",
        desc: "Every service record is stored on a dedicated cloud server, available on request for audits or warranty claims.",
        benefits: [
          "Cloud-stored records available on demand",
          "Audit- and warranty-ready documentation",
          "Complete service history per asset",
        ],
        pricing: [{ label: "Included with PM Program", rate: "Included", unit: "" }],
      },
      {
        slug: "pm-scheduling",
        title: "PM Scheduling",
        desc: "We schedule routine visits to your location and service the entire fleet based on your PM program.",
        benefits: [
          "Routine visits handled for you",
          "Whole-fleet servicing in one visit",
          "Set-and-forget scheduling",
        ],
        pricing: [{ label: "Included with PM Program", rate: "Included", unit: "" }],
      },
    ],
  },
  {
    category: "Shop Services",
    tag: "Drive-in repair capability",
    items: [
      {
        slug: "in-shop-maintenance",
        title: "In-Shop Maintenance",
        desc: "When it's more convenient to drive in, we have shop space available in certain areas. Pickup and delivery available.",
        benefits: [
          "Convenient drive-in service",
          "Controlled shop environment",
          "Pickup & delivery available",
        ],
        pricing: [
          { label: "Tractor & light-duty repair", rate: "$150", unit: "/ hour" },
          { label: "General labor · day rate", rate: "$75", unit: "/ hour" },
        ],
      },
      {
        slug: "heavy-repairs",
        title: "Heavy Repairs",
        desc: "Our shop provides all major repairs on all brands of cars, trucks, and equipment. Pickup and delivery available.",
        benefits: [
          "All major repairs handled",
          "Every brand of car, truck & equipment",
          "Pickup & delivery available",
        ],
        pricing: [
          { label: "Heavy crane rate", rate: "$175", unit: "/ hour" },
          { label: "Tractor & light-duty", rate: "$150", unit: "/ hour" },
        ],
      },
      {
        slug: "component-swings",
        title: "Component Swings",
        desc: "Our Odessa shop has a crane and experienced staff for component exchange on oilfield equipment, specializing in frac equipment.",
        benefits: [
          "On-site crane capability",
          "Specialized frac equipment expertise",
          "Fast component exchange turnaround",
        ],
        pricing: [{ label: "Heavy crane rate", rate: "$175", unit: "/ hour" }],
      },
    ],
  },
  {
    category: "Industrial Cleaning",
    tag: "On-site industrial-grade cleaning",
    items: [
      {
        slug: "fleet-wash",
        title: "Fleet Wash",
        desc: "We bring everything needed to wash your entire fleet on-site, water, containment, and haul-off of the wash water.",
        benefits: [
          "Full on-site fleet washing",
          "We bring water and containment",
          "Wash water removed responsibly",
        ],
        pricing: [{ label: "Fleet Wash · on-site", rate: "$300", unit: "/ hour" }],
      },
      {
        slug: "steam-cleaning",
        title: "Steam Cleaning",
        desc: "High-heat, low-pressure wash service on-site for extreme jobs like drilling rigs, compression units, and frac crews between jobs, all with containment.",
        benefits: [
          "High-heat, low-pressure deep clean",
          "Built for rigs, compression & frac units",
          "Performed with full containment",
        ],
        pricing: [{ label: "Steam Wash · industrial cleaning · day rate", rate: "$350", unit: "/ hour" }],
      },
      {
        slug: "dry-ice-cleaning",
        title: "Dry Ice Cleaning",
        desc: "Dry-ice industrial cleaning removes contaminants from radiators, engines, and sensitive equipment with zero water and zero residue.",
        benefits: [
          "Non-abrasive, no water, no residue",
          "Safe around electronics & sensitive parts",
          "Restores thermal efficiency",
        ],
        pricing: [{ label: "Dry Ice · industrial cleaning · day rate", rate: "$350", unit: "/ hour" }],
      },
    ],
  },
  {
    category: "Inspections",
    tag: "Compliance you can trust",
    items: [
      {
        slug: "dot-inspections",
        title: "Federal Annual DOT Inspections",
        desc: "Federal Annual DOT inspections performed to keep your fleet compliant and road-legal.",
        benefits: [
          "Stay DOT-compliant",
          "Avoid violations and fines",
          "Documented inspection records",
        ],
        pricing: [{ label: "DOT Annual Inspection", rate: "Call for Quote", unit: "" }],
      },
      {
        slug: "texas-state-inspections",
        title: "Texas State Inspections",
        desc: "Texas State inspections handled on-site or in-shop to keep your assets legal.",
        benefits: [
          "State inspection compliance",
          "On-site or in-shop options",
          "Fast turnaround",
        ],
        pricing: [{ label: "Texas State Inspection", rate: "Call for Quote", unit: "" }],
      },
    ],
  },
  {
    category: "Fleet Management",
    tag: "Total visibility of your fleet",
    items: [
      {
        slug: "maintenance-reporting",
        title: "Maintenance Reporting",
        desc: "Services are reported to you the first business day after service, so you always know what was done.",
        benefits: ["Next-business-day reporting", "Clear record of every service", "No surprises"],
        pricing: [{ label: "Included with any service", rate: "Included", unit: "" }],
      },
      {
        slug: "maintenance-record-storage",
        title: "Maintenance Record Storage",
        desc: "All Mobile Maintenance records are stored on our remote server and available whenever you need them.",
        benefits: [
          "Secure remote record storage",
          "Retrieve records anytime",
          "Audit- and warranty-ready",
        ],
        pricing: [{ label: "Included with any service", rate: "Included", unit: "" }],
      },
      {
        slug: "customer-inspection-sheets",
        title: "Customer Inspection Sheets",
        desc: "We create a custom digital inspection or use a customer-provided inspection sheet, your process, our execution.",
        benefits: [
          "Custom digital inspections",
          "Use your own inspection sheets",
          "Consistent, documented results",
        ],
        pricing: [{ label: "Included with any service", rate: "Included", unit: "" }],
      },
      {
        slug: "assigned-account-representative",
        title: "Assigned Account Representative",
        desc: "A dedicated account representative who is always one call away.",
        benefits: [
          "Single point of contact",
          "Always one call away",
          "Knows your fleet and history",
        ],
        pricing: [{ label: "Included with any service", rate: "Included", unit: "" }],
      },
      {
        slug: "mobile-technician-app",
        title: "Mobile Technician App",
        desc: "Our techs use custom software with pictures, video, and AI tracking for every service.",
        benefits: [
          "Photo & video of every service",
          "AI-assisted tracking",
          "Full transparency on work performed",
        ],
        pricing: [{ label: "Included with any service", rate: "Included", unit: "" }],
      },
      {
        slug: "real-time-communication",
        title: "Real-Time Communication",
        desc: "We see what our techs see, anywhere in North America, with constant tracking and communication.",
        benefits: [
          "Live visibility across North America",
          "Constant tracking & communication",
          "Faster decisions in the field",
        ],
        pricing: [{ label: "Included with any service", rate: "Included", unit: "" }],
      },
    ],
  },
];

export const SERVICES_FLAT = SERVICE_GROUPS.flatMap((g) =>
  g.items.map((i) => ({ ...i, category: g.category }))
);

export const SERVICE_OPTIONS = SERVICES_FLAT.map((s) => s.title);

export const RESOURCES = [
  {
    slug: "why-mobile-preventative-maintenance-saves-fleets",
    title: "Why Mobile Preventative Maintenance Saves Fleets Thousands Per Asset",
    excerpt: "Reactive repairs cost 3–5x more than scheduled PM. Here's how on-site preventative maintenance protects your margins and your uptime.",
    category: "Preventative Maintenance",
    read: "6 min read",
    date: "Dec 2025",
    body: [
      "Every hour a truck or piece of heavy equipment sits idle is an hour it isn't generating revenue. For fleet operators, unplanned downtime is one of the largest hidden costs on the balance sheet, and the vast majority of it is preventable.",
      "Mobile preventative maintenance (PM) flips the traditional model. Instead of hauling assets to a shop and waiting in a queue, fully-equipped mobile units come to your yard or jobsite on a scheduled cadence. Fluids are checked, wear items are replaced before they fail, and issues are caught while they're still cheap to fix.",
      "Industry data consistently shows that reactive, breakdown-driven repairs cost three to five times more than the equivalent scheduled service, once you factor in towing, expedited parts, overtime labor, and lost production. A structured PM program turns those unpredictable spikes into a flat, budgetable line item.",
      "The compounding benefit is asset longevity. Engines, cooling systems, and drivetrains that receive consistent care routinely outlast neglected equipment by years, protecting resale value and delaying major capital replacement.",
      "The takeaway for fleet managers: PM isn't an expense to minimize, it's the highest-ROI maintenance decision you can make. Build the program, keep the documentation, and let the data prove the savings.",
    ],
  },
  {
    slug: "dry-ice-vs-steam-radiator-cleaning",
    title: "Dry Ice vs. Steam Radiator Cleaning: Which Is Right for Your Equipment?",
    excerpt: "A clogged radiator quietly kills efficiency and shortens engine life. Compare dry-ice blasting and steam cleaning for your fleet.",
    category: "Cooling Systems",
    read: "5 min read",
    date: "Dec 2025",
    body: [
      "A radiator packed with dust, chaff, and oily debris can't shed heat, and an engine that runs hot loses power, burns more fuel, and fails early. Keeping cooling systems clean is one of the most overlooked levers in fleet maintenance.",
      "Steam radiator cleaning uses high-heat, high-pressure steam to break down grease and packed debris. It's highly effective for heavily contaminated units and pairs naturally with a full fleet wash, leaving equipment visibly restored.",
      "Dry-ice cleaning takes a different approach: solid CO2 pellets are blasted at the radiator core, freezing and lifting contaminants without water, chemicals, or residue. Because it's non-abrasive and completely dry, it's ideal for sensitive electrical areas and situations where water runoff is a concern.",
      "The right choice depends on contamination type, environment, and downtime tolerance. Many fleets use both, steam for scheduled deep cleans and dry ice for precision work around electronics.",
      "Whichever method you choose, restoring thermal efficiency pays for itself in fuel savings and extended engine life.",
    ],
  },
  {
    slug: "building-a-nationwide-fleet-maintenance-program",
    title: "Building a Nationwide Fleet Maintenance Program That Actually Scales",
    excerpt: "Managing maintenance across states means fighting inconsistency. Here's the framework for standardizing quality at scale.",
    category: "Fleet Strategy",
    read: "7 min read",
    date: "Nov 2025",
    body: [
      "When your assets operate across multiple states, the biggest enemy isn't any single breakdown, it's inconsistency. Different vendors, different standards, and fragmented records make it nearly impossible to see the true health of your fleet.",
      "The foundation of a scalable program is a single, standardized PM specification applied everywhere you operate. Every technician follows the same multi-point process, uses the same documentation, and reports into the same system.",
      "Centralized inventory management is the second pillar. When parts are stocked and staged based on your actual fleet composition, the right component is on the truck before the technician arrives, eliminating the parts-runs that turn a one-hour job into a one-day delay.",
      "Finally, quality control closes the loop. Documented inspections and reporting give leadership real visibility: which assets are trending toward failure, which locations need attention, and where the maintenance budget is actually going.",
      "A national footprint doesn't have to mean chaos. With one partner, one standard, and one source of truth, scale becomes a strength instead of a liability.",
    ],
  },
];


export const CAREERS_EMAIL = "careers@mobile-maintenance.com";

export const JOBS = [
  {
    slug: "mobile-mechanic-texas",
    title: "Mobile Mechanic",
    location: "Texas",
    type: "Full-Time",
    team: "Field Service",
    summary:
      "Field-based diesel and heavy-equipment mechanic operating a fully-equipped Mobile Maintenance service truck. You bring the shop to the customer.",
    responsibilities: [
      "Perform on-site diagnostics, PMs, and repairs on trucks and heavy equipment at customer yards and jobsites",
      "Operate and maintain a company service truck stocked with tools, parts, and diagnostic software",
      "Complete digital inspection sheets and photo/video documentation for every service call",
      "Communicate directly with dispatch, account reps, and customers in the field",
    ],
    requirements: [
      "3+ years diesel / heavy-equipment mechanic experience",
      "Own hand tools (specialty tools provided)",
      "Valid Texas driver's license and clean MVR; DOT medical card a plus",
      "Comfortable working outdoors in all conditions",
    ],
  },
  {
    slug: "shop-mechanic",
    title: "Shop Mechanic",
    location: "Odessa, TX",
    type: "Full-Time",
    team: "Shop Services",
    summary:
      "Shop-based mechanic handling heavy repairs, component swings, and drive-in service at our Odessa facility. Every brand of car, truck, and equipment.",
    responsibilities: [
      "Perform heavy repairs and component exchanges in the Odessa shop",
      "Operate shop crane and heavy-lift equipment safely",
      "Diagnose issues across all brands of cars, trucks, and heavy equipment",
      "Document work performed and parts used in our maintenance system",
    ],
    requirements: [
      "3+ years shop mechanic experience, diesel or heavy-equipment strongly preferred",
      "Diagnostic experience across multiple brands",
      "Crane / heavy-lift experience a plus (especially frac equipment)",
      "Valid driver's license",
    ],
  },
  {
    slug: "pm-lead-technician-texas",
    title: "PM Lead Technician",
    location: "Texas",
    type: "Full-Time",
    team: "Preventative Maintenance",
    summary:
      "Lead a preventative-maintenance crew executing scheduled fleet visits across Texas. You own the route, the quality, and the customer relationship on-site.",
    responsibilities: [
      "Lead PM crews on scheduled customer visits, full-fleet PMs in a single visit",
      "Own quality control: inspection sheets, photos, and cloud-stored service records",
      "Mentor and train junior technicians in the field",
      "Coordinate with the assigned account rep and dispatch to keep the route on schedule",
    ],
    requirements: [
      "5+ years preventative-maintenance experience",
      "Prior crew-lead or lead-tech experience",
      "Strong communication and documentation habits",
      "Valid Texas driver's license and clean MVR",
    ],
  },
  {
    slug: "odessa-receptionist",
    title: "Receptionist",
    location: "Odessa, TX",
    type: "Full-Time",
    team: "Front Office",
    summary:
      "First point of contact at Mobile Maintenance HQ. Answer calls, greet visitors, and route customer requests to the right team fast.",
    responsibilities: [
      "Answer incoming calls and direct them to the appropriate account rep or dispatcher",
      "Greet visitors and coordinate walk-in service intake",
      "Support scheduling and light administrative tasks",
      "Maintain a professional, organized front-office environment",
    ],
    requirements: [
      "Strong verbal communication and customer-service skills",
      "Comfortable with Microsoft Office and scheduling tools",
      "Prior receptionist or front-desk experience preferred",
      "Reliable, punctual, and detail-oriented",
    ],
  },
  {
    slug: "new-braunfels-administrative-assistant",
    title: "Administrative Assistant",
    location: "New Braunfels, TX",
    type: "Full-Time",
    team: "Operations Support",
    summary:
      "Administrative and operations support for our New Braunfels location. Keeps records, invoicing, and scheduling running smoothly for the field team.",
    responsibilities: [
      "Support daily office operations, records, invoicing, and scheduling coordination",
      "Maintain service documentation and customer files",
      "Assist account reps and field techs with paperwork and follow-ups",
      "Handle correspondence with customers and vendors",
    ],
    requirements: [
      "2+ years administrative-assistant experience",
      "Strong organizational skills and attention to detail",
      "Proficiency in Microsoft Office / Google Workspace",
      "Familiarity with fleet-maintenance records is a plus",
    ],
  },
  {
    slug: "outside-sales-rep-texas",
    title: "Outside Sales Representative",
    location: "Texas",
    type: "Full-Time",
    team: "Sales",
    summary:
      "Field sales role calling on fleet operators, oilfield service companies, construction contractors, and transportation fleets across a Texas territory.",
    responsibilities: [
      "Prospect and close new fleet-maintenance contracts across an assigned Texas territory",
      "Build long-term relationships with fleet managers and operations leaders",
      "Coordinate with account reps and PM leads to onboard new customers",
      "Represent Mobile Maintenance at industry events and customer sites",
    ],
    requirements: [
      "3+ years B2B outside sales, fleet, industrial, oilfield, or heavy-equipment preferred",
      "Hunter mentality with proven new-business track record",
      "Comfortable managing a large Texas territory with regular travel",
      "Valid driver's license and clean MVR",
    ],
  },
  {
    slug: "fleet-wash-technician-texas",
    title: "Fleet Wash Technician",
    location: "Texas",
    type: "Full-Time",
    team: "Fleet Wash",
    summary:
      "On-site fleet-wash crew member operating industrial wash equipment, containment systems, and steam-cleaning gear at customer yards and jobsites.",
    responsibilities: [
      "Perform on-site fleet washing at customer locations across Texas",
      "Operate wash rigs, containment systems, and steam-cleaning equipment safely",
      "Handle wash-water containment and haul-off responsibly",
      "Document services and complete customer inspection sheets",
    ],
    requirements: [
      "Prior wash / detailing / industrial cleaning experience preferred",
      "Physically capable, extended standing, lifting, and outdoor work",
      "Valid driver's license; willingness to travel across Texas",
      "Reliable and safety-focused",
    ],
  },
  {
    slug: "pdt-technician-texas",
    title: "PDT Technician",
    location: "Texas",
    type: "Full-Time",
    team: "Preventative Maintenance",
    summary:
      "Preventative-Diagnostic Technician, runs scheduled diagnostics, PM checks, and detailed reporting on customer fleets. Detail-oriented, data-driven work.",
    responsibilities: [
      "Execute scheduled preventative-diagnostic routines on customer fleets",
      "Use diagnostic software and inspection tools to identify wear and pending failures",
      "Complete detailed digital inspection sheets and photo/video documentation",
      "Flag issues to lead technicians and account reps for follow-up service",
    ],
    requirements: [
      "Diesel / heavy-equipment mechanical background",
      "Comfortable with diagnostic software and digital reporting tools",
      "Strong attention to detail, documentation quality matters",
      "Valid Texas driver's license",
    ],
  },
  {
    slug: "e-tech-texas",
    title: "E-Tech (Electrical Technician)",
    location: "Texas",
    type: "Full-Time",
    team: "Field Service",
    summary:
      "Electrical / electronic technician specializing in heavy-equipment electrical diagnostics, wiring, and controls. Field-based across Texas.",
    responsibilities: [
      "Diagnose and repair electrical and electronic systems on trucks and heavy equipment",
      "Read wiring diagrams and troubleshoot control systems in the field",
      "Support other mobile mechanics with electrical / diagnostic escalations",
      "Document repairs and update service records",
    ],
    requirements: [
      "Automotive or heavy-equipment electrical experience",
      "Comfortable reading schematics and using diagnostic scanners",
      "Willingness to travel across Texas",
      "Valid driver's license and clean MVR",
    ],
  },
];
